import React, { useState, useMemo, useEffect, type JSX } from "react";
import { useAppSelector } from "../../hooks/reduxHooks";
import { authenticatedFetch } from "../../utils/authErrorHandler";
import DealModalShell from "./DealModalShell";
import DealGeneralTab from "./DealGeneralTab";
import DealDocumentsTab from "./DealDocumentsTab";
import type { DealData, DocumentData } from "./dealTypes";

type ModalTab = "General" | "Documents";

interface DealDetailsState {
  fullDealData: DealData | null;
  loading: boolean;
  error: string | null;
}

/** Fetch the full deal record whenever the opened deal changes. */
function useDealDetails(deal: DealData | null): DealDetailsState {
  const [fullDealData, setFullDealData] = useState<DealData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!deal || !deal.dealId) return;

    setFullDealData(null);
    setLoading(true);
    setError(null);

    const fetchDealDetails = async (): Promise<void> => {
      try {
        // Use the configured deals API base URL from environment variables
        const baseUrl = import.meta.env.VITE_DEALS_API_BASE_URL;
        const response = await authenticatedFetch(`${baseUrl}/api/objects/deals/${deal.dealId}`);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch deal details: ${response.status} ${response.statusText}`
          );
        }

        setFullDealData(await response.json());
      } catch (fetchError) {
        setError(
          fetchError instanceof Error ? fetchError.message : "Failed to fetch deal details"
        );
      } finally {
        setLoading(false);
      }
    };

    void fetchDealDetails();
  }, [deal]);

  return { fullDealData, loading, error };
}

/** Tab strip; the Documents tab carries the document count. */
function ModalTabs({
  activeTab,
  documentCount,
  onSelect,
}: {
  activeTab: ModalTab;
  documentCount: number;
  onSelect: (tab: ModalTab) => void;
}): JSX.Element {
  const className = (tab: ModalTab): string =>
    `relative inline-flex items-center py-2 px-5 text-sm font-semibold focus:outline-none transition-colors duration-200 ease-in-out ${
      activeTab === tab
        ? "border-b-2 border-indigo-600 text-indigo-600"
        : "text-gray-500 hover:text-gray-800 border-b-2 border-transparent"
    }`;

  return (
    <div className="flex-shrink-0 border-b border-gray-200">
      <button onClick={() => onSelect("General")} className={className("General")}>
        General
      </button>
      <button onClick={() => onSelect("Documents")} className={className("Documents")}>
        Documents ({documentCount})
      </button>
    </div>
  );
}

/** Spinner while the record loads, or the failure plus a retry. */
function LoadingOrError({ loading, error }: { loading: boolean; error: string }): JSX.Element {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading deal details...</span>
      </div>
    );
  }
  return (
    <div className="text-center py-8">
      <div className="text-red-600 mb-2">Failed to load deal details</div>
      <div className="text-sm text-gray-500">{error}</div>
      <button
        onClick={() => window.location.reload()}
        className="mt-3 px-4 py-2 bg-cvc-blue text-white rounded hover:bg-cvc-blue/90 transition-colors"
      >
        Retry
      </button>
    </div>
  );
}

/** Full deal record in a modal, with a General and a Documents tab. */
export default function DealDetailsModal({
  deal,
  onClose,
}: {
  deal: DealData | null;
  onClose: () => void;
}): JSX.Element | null {
  const [activeTab, setActiveTab] = useState<ModalTab>("General");
  const { fullDealData, loading, error } = useDealDetails(deal);

  // Documents come from Redux rather than the deal record
  const allDocuments = useAppSelector(
    (state) => state.resultsSlice.documents || []
  ) as DocumentData[];
  const dealDocuments = useMemo(() => {
    if (!deal?.dealId) return [];
    return allDocuments.filter((doc) => doc.dealId === deal.dealId);
  }, [allDocuments, deal?.dealId]);

  if (!deal) return null;

  // Fall back to the row's own data until the full record lands
  const displayData = fullDealData || deal;

  return (
    <DealModalShell
      title={displayData.dealName || "Loading..."}
      onClose={onClose}
      heightClass="h-[90vh]"
    >
      <ModalTabs
        activeTab={activeTab}
        documentCount={dealDocuments.length}
        onSelect={setActiveTab}
      />
      <div className="p-6 overflow-y-auto">
        {loading || error ? (
          <LoadingOrError loading={loading} error={error || ""} />
        ) : activeTab === "General" ? (
          <DealGeneralTab deal={displayData} />
        ) : (
          <DealDocumentsTab documents={dealDocuments} onClose={onClose} />
        )}
      </div>
    </DealModalShell>
  );
}
